/**
 * "Setup progress" card on the dashboard (tracker item 75), ADMIN only: the
 * guided setup checklist with a progress bar, the steps (done, skipped or to
 * do) linking to the page that configures each, Skip / Unskip, Mark done for
 * the two steps no data can prove, and Hide checklist. Shows nothing while
 * loading, when the checklist is hidden, or when it could not be loaded.
 *
 * Also the entry to the /setup guide ("Open setup guide") and the first-login
 * redirect: the first time in a browser session an ADMIN lands on the
 * dashboard with a visible (not hidden, not complete) checklist, they are sent
 * to /setup, once. Nobody who must change a temporary password or enroll in
 * two-step verification first is asked anything (no request is made).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createAPIClient, ISetupApi } from '../api';
import * as types from '../api/types';
import { useAuth } from '../context/AuthContext';
import { canManageSetup } from '../utils/permissions';
import { getErrorMessage } from '../utils/apiError';
import { SETUP_PATH, markSetupRedirectDone, setupRedirectDone } from '../utils/setupWizard';
import { STARTER_PACKS_PATH } from '../utils/starterUi';
import { Toast } from './Toast';

const smallButton =
  'text-sm font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50 min-h-[44px] sm:min-h-0 px-2 sm:px-0 -mx-2 sm:mx-0';

function stateLabel(step: types.SetupStep): string {
  if (step.done) return 'Done';
  return step.skipped ? 'Skipped' : 'To do';
}

export function SetupChecklistCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // The backend refuses every route but the password / MFA enrollment screens while either gate applies
  const gated = Boolean(user?.mustChangePassword || user?.mfaEnrollmentRequired);
  const allowed = canManageSetup(user) && !gated;
  const userId = user?.id;
  const api = useMemo<ISetupApi>(() => createAPIClient(), []);
  const [progress, setProgress] = useState<types.SetupProgress | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only an ADMIN may read the checklist: nobody else makes the request
  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    api
      .getSetup()
      .then((result) => {
        if (!cancelled) setProgress(result);
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err, 'The setup checklist could not be loaded.'));
      });
    return () => {
      cancelled = true;
    };
  }, [api, allowed]);

  // First login of the browser session: send an ADMIN with an open checklist to the guide, once (no loop).
  // Only the first load decides; a later change on this page (e.g. an unskip) never redirects.
  const redirectDecided = useRef(false);
  useEffect(() => {
    if (!allowed || !userId || !progress || redirectDecided.current) return;
    redirectDecided.current = true;
    if (setupRedirectDone(userId)) return;
    markSetupRedirectDone(userId);
    if (!progress.dismissed && !progress.complete) navigate(SETUP_PATH);
  }, [allowed, userId, progress, navigate]);

  const change = useCallback(
    async (action: () => Promise<types.SetupProgress>, failure: string) => {
      setBusy(true);
      setError(null);
      try {
        setProgress(await action());
      } catch (err) {
        setError(getErrorMessage(err, failure));
      } finally {
        setBusy(false);
      }
    },
    []
  );

  if (!allowed) return null;
  const errorToast = error && <Toast kind="error">{error}</Toast>;
  if (!progress || progress.dismissed) return <>{errorToast}</>;

  const hide = () => change(() => api.setSetupDismissed(true), 'The setup checklist could not be hidden.');

  if (progress.complete) {
    return (
      <section className="bg-white rounded-lg shadow p-4 sm:p-6" aria-labelledby="setup-progress-heading">
        {errorToast}
        <h2 id="setup-progress-heading" className="text-xl font-bold text-gray-900">Setup progress</h2>
        <p className="mt-2 text-gray-700">Setup is complete. You can reopen the checklist any time from Settings.</p>
        <button type="button" onClick={hide} disabled={busy} className={`mt-3 ${smallButton}`}>
          Hide checklist
        </button>
      </section>
    );
  }

  return (
    <section className="bg-white rounded-lg shadow p-4 sm:p-6" aria-labelledby="setup-progress-heading">
      {errorToast}
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h2 id="setup-progress-heading" className="text-xl font-bold text-gray-900">Setup progress</h2>
        <button type="button" onClick={hide} disabled={busy} className={smallButton}>
          Hide checklist
        </button>
      </div>
      <p className="mt-1 text-sm text-gray-600">
        {progress.resolvedCount} of {progress.total} steps done or skipped
        <Link to={SETUP_PATH} className="ml-3 inline-flex items-center min-h-[44px] sm:min-h-0 font-semibold text-blue-600 hover:text-blue-700 hover:underline">
          Open setup guide
        </Link>
      </p>
      <div
        role="progressbar"
        aria-label="Setup progress"
        aria-valuemin={0}
        aria-valuemax={progress.total}
        aria-valuenow={progress.resolvedCount}
        aria-valuetext={`${progress.resolvedCount} of ${progress.total} steps done or skipped`}
        className="mt-2 h-2 w-full rounded-full bg-gray-200 overflow-hidden"
      >
        <div className="h-full bg-blue-600" style={{ width: `${progress.total ? (progress.resolvedCount / progress.total) * 100 : 0}%` }} />
      </div>

      <ol className="mt-4 divide-y divide-gray-100">
        {progress.steps.map((step) => (
          <li key={step.key} data-step={step.key} data-state={stateLabel(step).toLowerCase().replace(' ', '-')} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
            <span
              className={`w-16 shrink-0 text-xs font-semibold uppercase tracking-wide ${step.done ? 'text-green-700' : step.skipped ? 'text-gray-500' : 'text-amber-700'}`}
            >
              {stateLabel(step)}
            </span>
            <div className="min-w-0 flex-1">
              <Link
                to={step.link}
                className={`inline-flex items-center min-h-[44px] sm:min-h-0 font-medium break-words hover:underline ${step.done || step.skipped ? 'text-gray-500' : 'text-blue-600'}`}
              >
                {step.title}
              </Link>
              {step.optional && (
                <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-600">Optional</span>
              )}
              {step.key === 'items' && !step.done && (
                <Link to={STARTER_PACKS_PATH} className="ml-3 inline-flex items-center min-h-[44px] sm:min-h-0 text-sm font-semibold text-blue-600 hover:text-blue-700 hover:underline">
                  Start from an industry pack
                </Link>
              )}
            </div>
            <div className="flex items-center gap-3">
              {step.manual && !step.done && (
                <button
                  type="button"
                  className={smallButton}
                  disabled={busy}
                  aria-label={`Mark done: ${step.title}`}
                  onClick={() => change(() => api.markSetupStepDone(step.key), 'The step could not be marked done.')}
                >
                  Mark done
                </button>
              )}
              {step.manual && step.done && (
                <button
                  type="button"
                  className={smallButton}
                  disabled={busy}
                  aria-label={`Undo mark done: ${step.title}`}
                  onClick={() => change(() => api.unmarkSetupStepDone(step.key), 'The step could not be changed.')}
                >
                  Undo
                </button>
              )}
              {!step.done && !step.skipped && (
                <button
                  type="button"
                  className={smallButton}
                  disabled={busy}
                  aria-label={`Skip: ${step.title}`}
                  onClick={() => change(() => api.skipSetupStep(step.key), 'The step could not be skipped.')}
                >
                  Skip
                </button>
              )}
              {step.skipped && (
                <button
                  type="button"
                  className={smallButton}
                  disabled={busy}
                  aria-label={`Unskip: ${step.title}`}
                  onClick={() => change(() => api.unskipSetupStep(step.key), 'The step could not be unskipped.')}
                >
                  Unskip
                </button>
              )}
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}
